// Utilities to build unified share messages across templates

/**
 * Build the unified share message text.
 * Example: "Check out Eric Daniels 's digital business card https://ericd.tcards.org/ericd1"
 * Note: The space before `'s` is intentional to match the classic template wording.
 */
export function buildShareMessage(name: string, url: string): string {
  const n = String(name || '').trim() || 'My';
  const u = String(url || '').trim();
  const possessive = `${n} 's`;
  const base = `Check out ${possessive} digital business card`;
  return u ? `${base} ${u}` : base;
}

/**
 * Build a payload for the Web Share API.
 * Returns title, text, and url fields, using the unified message for text.
 */
export function buildSharePayload(opts: { name?: string; url: string }) {
  const name = String(opts?.name || '').trim();
  const url = String(opts?.url || '').trim();
  // IMPORTANT: Do NOT include the URL in the text for Web Share API.
  // Many apps (e.g., WhatsApp) automatically append the `url` field to the
  // composed message, which would otherwise duplicate the link if it already
  // exists inside `text`.
  const possessive = `${(name || 'My')} 's`;
  const text = `Check out ${possessive} digital business card`;
  // Keep title simple; many apps will show only text + url
  const title = name ? `${name} — Digital Business Card` : 'Digital Business Card';
  return { title, text, url };
}
export function getHomepageShareUrl(root: string = 'tcards.org') {
  return `https://${root}/`;
}

// Card share link (preview shim)
export function getCardShareUrl(handle: string, username: string, root: string = 'tcards.org') {
  // For public sharing we prefer the canonical subdomain, but some flows still
  // use the /o/ shim. Keep this path, but downstream we prefer canonicalCardUrl.
  const h = encodeURIComponent(String(handle).replace(/^www\./, ''));
  const u = encodeURIComponent(username);
  return `https://${root}/o/${h}/${u}`;
}

/** Full share text including URL when message omits it. */
export function resolveShareText(url: string, text?: string): string {
  const u = String(url || '').trim();
  const t = String(text || '').trim();
  if (!t) return u;
  if (u && !t.includes(u)) return `${t} ${u}`.trim();
  return t;
}

/** Platform share URLs for the card share sheet. */
export function buildSocialShareLinks(opts: { url: string; text?: string; title?: string }) {
  const url = String(opts.url || '').trim();
  const text = String(opts.text || '').trim();
  const title = String(opts.title || 'Digital Business Card').trim();
  const fullText = resolveShareText(url, text);
  const encodedUrl = encodeURIComponent(url);
  const encodedText = encodeURIComponent(text || fullText);
  const encodedFull = encodeURIComponent(fullText);
  const encodedTitle = encodeURIComponent(title);
  return {
    whatsapp: `https://wa.me/?text=${encodedFull}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    twitter: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    telegram: `https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`,
    email: `mailto:?subject=${encodedTitle}&body=${encodedFull}`,
    sms: `sms:?&body=${encodedFull}`,
  };
}